import { compareVersions } from 'compare-versions'
import { Setting2 } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sidebar } from 'react-pro-sidebar'
import { useSelector } from 'react-redux'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'

import backgroundImage1 from '@/assets/wecheckLights/1.png'
import backgroundImage2 from '@/assets/wecheckLights/2.png'
import { Loader } from '@/components/Loader/Loader'
import { AppRoutes } from '@/constants/appRoutes'
import { MobileBackProvider, useMobileBack } from '@/contexts/MobileBackContext'
import env from '@/env/env'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useCheckSession } from '@/hooks/useCheckSession'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import {
  EUserPermission,
  EUserRole,
} from '@/hooks/usePermissions/permissions.type'
import { usePermissions } from '@/hooks/usePermissions/usePermissions'
import useSmallMobileMediaQuery from '@/hooks/useSmallMobileMediaQuery'
import useTabletMediaQuery from '@/hooks/useTabletMediaQuery'
import { cn } from '@/lib/utils'
import { selectForceReloadVersion } from '@/store/api/Config/Config.selector'
import { selectIsNavBarCollapsed } from '@/store/api/Ui/Ui.selector'
import { useLazyUserInfoQuery } from '@/store/api/Users/Users.query'
import {
  selectIsPending,
  selectUserFirstLetter,
  selectUserFullname,
} from '@/store/api/Users/Users.selector'
import { setUserInfo, setWorkspaceCredits } from '@/store/api/Users/Users.slice'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

import { ForceReloadCard } from '../Home/components/ForceReloadCard/ForceReloadCard'

import { MobileTopBar } from './components/MobileTopBar/MobileTopBar'
import { SidebarItem } from './components/sidebarItem/SidebarItem'
import { SidebarUser } from './components/sidebarUser/sidebarUser'
import { WecheckEnvironmentDistinction } from './components/WecheckEnvironmentDistinction/WecheckEnvironmentDistinction'
import WorkstationSelector from './components/WorkstationSelector/WorkstationSelector'

const SETTINGS_BASE = '/dashboard/settings'
const isSettingsSubpage = (pathname: string) =>
  pathname.startsWith(`${SETTINGS_BASE}/`)

const SIDEBAR_WIDTH = '275px'

export const LeftSideBar = () => (
  <MobileBackProvider>
    <LeftSideBarInner />
  </MobileBackProvider>
)

const LeftSideBarInner = () => {
  const { hasPermission } = usePermissions()
  const location = useLocation()
  const navigate = useNavigate()
  const locationPathname = location.pathname
  const dispatch = useAppDispatch()
  const environment = env.NODE_ENV
  const { t } = useTranslation()
  const { onBack: contextBack, useHomeIcon: contextUseHomeIcon } =
    useMobileBack()

  const isMobile = useMobileMediaQuery()
  const isSmallMobile = useSmallMobileMediaQuery()
  const isTablet = useTabletMediaQuery()
  const isSmallDevice = isMobile || isSmallMobile || isTablet

  const remoteConfigForceReloadVersion = useSelector(selectForceReloadVersion)
  const localVersion = env.APP_VERSION

  const isCreateReportFlow = locationPathname.startsWith(
    '/dashboard/run-report/',
  )

  const isFullscreenRoute =
    locationPathname === AppRoutes.generatingReport ||
    locationPathname.startsWith('/dashboard/run-report/')

  const [broken, setBroken] = useState(false)
  const [toggled, setToggled] = useState(false)
  const collapsed = useAppSelector(selectIsNavBarCollapsed)

  useCheckSession()

  const [triggerGetUserInfo, { isLoading: isLoadingUserData, data: userData }] =
    useLazyUserInfoQuery({ pollingInterval: 300000 })

  const isPending = selectIsPending(userData)
  const userFullname = selectUserFullname(userData)
  const userFirstLetter = selectUserFirstLetter(userData)
  const {
    i18n: { changeLanguage },
  } = useTranslation()

  useEffect(() => {
    triggerGetUserInfo()
  }, [locationPathname, triggerGetUserInfo])

  useEffect(() => {
    if (userData?.enterprise?.credits) {
      dispatch(setWorkspaceCredits(userData?.enterprise?.credits))
    }
  }, [userData])

  useEffect(() => {
    if (userData) {
      dispatch(setUserInfo(userData))
      changeLanguage(userData.language)
    }
  }, [userData])

  if (isLoadingUserData || !userData) {
    return (
      <div className="flex h-screen items-center">
        <div className="flex w-full justify-center">
          <Loader />
        </div>
      </div>
    )
  }

  // Fullscreen routes: render only the page content without sidebar
  // (keeps the animated background images for visual continuity)
  if (isFullscreenRoute) {
    return (
      <div className="relative h-screen w-screen overflow-hidden bg-baseColor">
        <div
          className="fixed inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage1})`,
            zIndex: 0,
            height: '100%',
          }}
        />
        <div
          className="absolute inset-0 animate-bg-scroll bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage2})`,
            zIndex: 0,
            height: '100%',
          }}
        />
        <div className="relative z-10 flex h-full flex-col">
          {isSmallDevice && (
            <MobileTopBar
              environment={environment}
              onBack={contextBack ?? undefined}
              useHomeIcon={contextUseHomeIcon}
            />
          )}
          <Outlet />
        </div>
      </div>
    )
  }

  const isAccountActive = locationPathname === AppRoutes.settings

  return (
    <div className="h-screen">
      <div className="flex h-full w-full">
        <Sidebar
          toggled={toggled}
          width={SIDEBAR_WIDTH}
          className="w-full"
          onBreakPoint={setBroken}
          onBackdropClick={() => setToggled(false)}
          breakPoint="md"
          collapsed={collapsed}
          backgroundColor={weCheckPalette.baseColor}
          style={{ borderColor: weCheckPalette.baseColor }}
        >
          <div className="flex h-full w-full flex-col">
            <div className="flex h-14 items-center px-4">
              <WecheckEnvironmentDistinction environment={environment} />
            </div>

            <div className="border-y border-[#0000001a] dark:border-[#ffffff14]">
              <WorkstationSelector />
            </div>

            <nav className="flex flex-1 flex-col gap-3 p-2">
              <SidebarItem
                name={t('dashboard.sidebar.runSearch')}
                isActive={
                  locationPathname === AppRoutes.dashboard ||
                  locationPathname === AppRoutes.faceSearch ||
                  locationPathname === AppRoutes.generatingReport
                }
                linkUrl={AppRoutes.dashboard}
                disabled={
                  !hasPermission(
                    EUserPermission.HANDLE_REPORTS,
                    EUserRole.TEAM_ANALYST,
                  )
                }
                collapsed={collapsed}
                customOnClick={() => setToggled(false)}
              />
              <SidebarItem
                name={t('dashboard.sidebar.reportHistory')}
                isActive={
                  locationPathname === AppRoutes.reports ||
                  locationPathname === AppRoutes.yourReports ||
                  locationPathname === AppRoutes.faceMatchResults ||
                  locationPathname === AppRoutes.digitalReport ||
                  locationPathname === AppRoutes.postList ||
                  locationPathname === AppRoutes.socialMediaProfile
                }
                linkUrl={AppRoutes.reports}
                disabled={
                  !hasPermission(EUserPermission.VIEW_REPORTS, EUserRole.READER)
                }
                collapsed={collapsed}
                customOnClick={() => setToggled(false)}
              />

              {hasPermission(
                EUserPermission.HANDLE_TEAMS,
                EUserRole.TEAM_MANAGER,
              ) && (
                <SidebarItem
                  name={t('dashboard.sidebar.company')}
                  isActive={
                    locationPathname === AppRoutes.enterprise ||
                    locationPathname === AppRoutes.roleGuide ||
                    locationPathname === AppRoutes.workspaceSettings ||
                    locationPathname === AppRoutes.teamSelection ||
                    locationPathname === AppRoutes.createTeamFirstStep ||
                    locationPathname === AppRoutes.createTeamSecondStep ||
                    locationPathname === AppRoutes.workspaceCreation ||
                    locationPathname ===
                      AppRoutes.workspaceCreationSecondStep ||
                    locationPathname === AppRoutes.workspaceCreationThirdStep
                  }
                  linkUrl={AppRoutes.enterprise}
                  disabled={false}
                  collapsed={collapsed}
                  customOnClick={() => setToggled(false)}
                />
              )}

              {hasPermission(
                EUserPermission.HANDLE_CREDITS,
                EUserRole.CO_OWNER,
              ) && (
                <SidebarItem
                  name={t('dashboard.sidebar.accountBalance')}
                  isActive={locationPathname === AppRoutes.billing}
                  linkUrl={AppRoutes.billing}
                  disabled={false}
                  collapsed={collapsed}
                  customOnClick={() => setToggled(false)}
                />
              )}

              <SidebarItem
                name={t('dashboard.sidebar.support')}
                isActive={locationPathname === AppRoutes.support}
                linkUrl={AppRoutes.support}
                disabled={false}
                collapsed={collapsed}
                customOnClick={() => setToggled(false)}
              />

              <SidebarItem
                name={t('dashboard.sidebar.batchSearch')}
                isActive={locationPathname === AppRoutes.batchSearch}
                linkUrl={AppRoutes.batchSearch}
                disabled={
                  !hasPermission(
                    EUserPermission.HANDLE_REPORTS,
                    EUserRole.TEAM_ANALYST,
                  )
                }
                collapsed={collapsed}
                customOnClick={() => setToggled(false)}
              />
            </nav>

            <div className="border-y border-[#0000001a] dark:border-[#ffffff14] p-2">
              <Link
                to={AppRoutes.settings}
                role="button"
                className={cn(
                  'flex items-center justify-between rounded-xl p-3 transition-colors',
                  isAccountActive
                    ? 'bg-baseColorHighlight'
                    : 'hover:bg-baseColorHighlight',
                )}
              >
                <span className="text-base font-medium text-neutralColor">
                  {t('dashboard.sidebar.account')}
                </span>
                <Setting2
                  size={20}
                  variant="Bold"
                  color={
                    isAccountActive
                      ? weCheckPalette.neutralColor
                      : weCheckPalette.neutralColor60
                  }
                />
              </Link>
            </div>

            <div className="p-2">
              <SidebarUser
                mail={userData.email}
                name={userFullname}
                userInitials={userFirstLetter}
                isPending={isPending}
              />
            </div>
          </div>
        </Sidebar>

        {!isSmallDevice && (
          <>
            {compareVersions(
              remoteConfigForceReloadVersion.version,
              localVersion,
            ) > 0 && (
              <div
                className="fixed left-[275px] z-20"
                style={{ width: `calc(100vw - ${SIDEBAR_WIDTH})` }}
              >
                <ForceReloadCard />
              </div>
            )}
          </>
        )}
        {isSmallDevice && (
          <>
            {compareVersions(
              remoteConfigForceReloadVersion.version,
              localVersion,
            ) > 0 && (
              <div className="fixed z-50 w-full">
                <ForceReloadCard />
              </div>
            )}
          </>
        )}

        <main className="flex h-full w-full flex-col">
          <div className="custom-scrollbar relative w-full flex-1 min-h-0 overflow-auto bg-transparent via-15% flex flex-col">
            <div
              className="fixed inset-0 bg-cover bg-center bg-no-repeat md:ml-[275px]"
              style={{
                backgroundImage: `url(${backgroundImage1})`,
                zIndex: -1,
                height: '100%',
              }}
            />
            <div
              className="absolute inset-0 animate-bg-scroll bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${backgroundImage2})`,
                zIndex: -1,
                height: '100%',
              }}
            />

            {broken && (
              <MobileTopBar
                environment={environment}
                onMenuClick={() => setToggled(!toggled)}
                onBack={
                  contextBack ??
                  (isSettingsSubpage(locationPathname)
                    ? () => navigate(AppRoutes.settings)
                    : locationPathname === AppRoutes.digitalReport
                      ? () => navigate(AppRoutes.reports)
                      : undefined)
                }
                useHomeIcon={contextUseHomeIcon}
              />
            )}

            <div className="relative z-10 flex-1 min-h-0">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
